import React, { useEffect, useState } from "react";
import { Send, Loader2, Mail, Clock } from "lucide-react";
import api from "../api";

const ReminderRow = ({ which, title }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get(`/admin/campaigns/new-customer/followup/${which}`).then(({ data }) => setSt(data)).catch(() => setSt(null)); }, [which]);
  const test = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/admin/campaigns/new-customer/followup/${which}/send-test`, { to_email: "support@prosourcelabs.org" });
      setMsg(data.ok ? `${title} test sent to ${data.sent_to} — greeted as "${data.greeting || "(no name on file)"}".` : "Test failed.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Test failed."); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`nc-${which}`} className="mt-6 pt-5 border-t border-[#1a1a26]">
      <div className="overline flex items-center gap-2"><Clock size={12} /> {title.toUpperCase()} · AUTOMATIC</div>
      <p className="text-xs text-[#8A94A6] mt-2 font-mono-tech">
        Sends automatically at <span data-testid={`nc-${which}-at`} className="text-white">{st?.scheduled_for || "…"}</span> to every registered non-buyer.
        {" "}Already sent: <span data-testid={`nc-${which}-sent`} className="text-white">{st ? st.already_sent : "…"}</span>
        {" "}· Will send: <span data-testid={`nc-${which}-will-send`} className="text-[#00C3FF]">{st ? st.will_send : "…"}</span>
      </p>
      <button data-testid={`nc-${which}-test`} onClick={test} disabled={busy} className="btn-ghost text-[10px] mt-3" style={{ padding: "10px 16px" }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send {title.toLowerCase()} test to support@
      </button>
      {msg && <div data-testid={`nc-${which}-msg`} className="mt-3 text-xs text-[#8A94A6] font-mono-tech">{msg}</div>}
    </div>
  );
};

// Admin → Campaigns: one-click FIRSTLAB25 welcome offer to registered customers with zero orders.
export default function NewCustomerOfferPanel() {
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/campaigns/new-customer/audience").then(({ data }) => setAud(data)).catch(() => setAud(null));
  useEffect(() => { load(); }, []);

  const test = async () => {
    setBusy("test"); setMsg("");
    try {
      const { data } = await api.post("/admin/campaigns/new-customer/send-test", { to_email: "support@prosourcelabs.org" });
      setMsg(data.ok ? `Test sent to ${data.sent_to} — greeted as "${data.greeting || "(no name on file)"}".` : "Test failed.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Test failed."); } finally { setBusy(""); }
  };
  const send = async () => {
    // eslint-disable-next-line no-alert
    const typed = window.prompt(`Send the 25% welcome offer (FIRSTLAB25) to ${aud?.will_send || 0} registered customer(s) with NO orders?\n\nCustomers who already received it are skipped.\nType SEND to confirm.`);
    if (typed !== "SEND") return;
    setBusy("send"); setMsg("");
    try {
      const { data } = await api.post("/admin/campaigns/new-customer/send", { confirm: "SEND" });
      setMsg(`Sending to ${data.queued} customer(s) now — this runs in the background.`);
      setTimeout(load, 5000);
    } catch (e) { setMsg(e?.response?.data?.detail || "Send failed."); } finally { setBusy(""); }
  };

  return (
    <div data-testid="new-customer-offer-panel" className="border border-[#00C3FF]/30 bg-[#00C3FF]/[0.03] p-6 mb-10">
      <div className="overline flex items-center gap-2"><Mail size={12} /> NEW-CUSTOMER WELCOME OFFER · FIRSTLAB25</div>
      <p className="text-sm text-[#8A94A6] mt-3 max-w-2xl">25% off a first order, valid only until 8 p.m. PT 10/31/26, not combinable with other codes, not valid for wholesale accounts or orders (stacks with the 5% Research Stack discount). Goes ONLY to registered (age-verified) customers with no orders or invoices of any kind. Unsubscribed and internal addresses are excluded. Each customer receives it once.</p>
      <div className="flex flex-wrap gap-6 mt-4 font-mono-tech text-xs">
        <div>Eligible: <span data-testid="nc-eligible" className="text-white">{aud ? aud.eligible : "…"}</span></div>
        <div>Already sent: <span data-testid="nc-already" className="text-white">{aud ? aud.already_sent : "…"}</span></div>
        <div>Will send: <span data-testid="nc-will-send" className="text-[#00C3FF]">{aud ? aud.will_send : "…"}</span></div>
      </div>
      <div className="flex flex-wrap gap-3 mt-5">
        <button data-testid="nc-send-test" onClick={test} disabled={!!busy} className="btn-ghost text-[10px]" style={{ padding: "10px 16px" }}>
          {busy === "test" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send test to support@
        </button>
        <button data-testid="nc-send-all" onClick={send} disabled={!!busy || !aud?.will_send} className="btn-primary text-[10px]" style={{ padding: "10px 16px" }}>
          {busy === "send" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to {aud?.will_send || 0} customers
        </button>
      </div>
      {msg && <div data-testid="nc-msg" className="mt-3 text-xs text-[#8A94A6] font-mono-tech">{msg}</div>}
      <ReminderRow which="reminder" title="48-Hour Reminder" />
      <ReminderRow which="lastcall" title="Last Call" />
      {aud?.recipients?.length > 0 && (
        <div data-testid="nc-recipient-list" className="mt-6 max-h-80 overflow-auto border border-[#1a1a26]">
          <table className="w-full text-xs font-mono-tech">
            <thead className="text-[#5C657A] text-left sticky top-0 bg-[#0C0C12]">
              <tr><th className="p-2">Email</th><th className="p-2">Name on file</th><th className="p-2">Greeting in email</th></tr>
            </thead>
            <tbody>
              {aud.recipients.map((r) => (
                <tr key={r.email} data-testid={`nc-recipient-${r.email}`} className="border-t border-[#1a1a26]">
                  <td className="p-2 text-[#8A94A6] break-all">{r.email}</td>
                  <td className="p-2 text-[#8A94A6]">{r.name_on_file || "—"}</td>
                  <td className="p-2 text-white">{r.greeting ? `Welcome, ${r.greeting}.` : "Welcome. (no name on file)"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
